import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import swal from "sweetalert";
import {
  FiArrowLeft,
  FiEdit3,
  FiCopy,
  FiTrash2,
  FiShield,
  FiMonitor,
  FiSmartphone,
  FiCode,
  FiCheck,
  FiTag,
  FiCalendar,
  FiLayers,
} from "react-icons/fi";

import Layout from "../../components/global/layout";
import loader from "../../methods/loader";
import {
  MODULE_CONFIG,
  EMAIL_TYPE_MAP,
  renderWithVariables,
  EmailTemplateService,
} from "./shared";
import { hasPermission } from "../../utils/permission";

export default function EmailTemplateDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.user);

  const canEdit = hasPermission(user, "email_templates", "edit");
  const canDelete = hasPermission(user, "email_templates", "delete");

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [testVariables, setTestVariables] = useState({});
  const [copiedHtml, setCopiedHtml] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      loader(true);
      setLoading(true);
      try {
        const res = await EmailTemplateService.detail(id);
        const data = res?.data || res?.template;
        if (data) {
          setTemplate(data);
          // Initialize test variables
          const initial = {};
          if (Array.isArray(data.variables)) {
            data.variables.forEach((v) => {
              initial[v.key] = v.example || `[${v.key}]`;
            });
          }
          setTestVariables(initial);
        } else {
          toast.error("Template not found.");
        }
      } catch (err) {
        console.error("Failed to load template detail:", err);
        toast.error("Failed to load template details.");
      } finally {
        setLoading(false);
        loader(false);
      }
    };

    fetchDetail();
  }, [id]);

  const handleDelete = async () => {
    if (!template) return;
    const name = template.name || "Email Template";

    if (template.is_default_backup) {
      swal({
        title: "Cannot Delete Default Backup",
        text: `"${name}" is marked as a Global Default Fallback Backup. Default backup templates cannot be deleted because they protect against email dispatch failures.`,
        icon: "warning",
        buttons: ["Understood", false],
        dangerMode: true,
      });
      return;
    }

    const confirmed = await swal({
      title: "Delete Email Template?",
      text: `Are you sure you want to permanently delete "${name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    });

    if (!confirmed) return;

    loader(true);
    try {
      const res = await EmailTemplateService.delete(template.id || template._id || id);
      if (res?.success) {
        toast.success("Template deleted successfully.");
        navigate(MODULE_CONFIG.routeBase);
      } else {
        toast.error(res?.message || "Failed to delete template.");
      }
    } catch (err) {
      toast.error("An error occurred while deleting the template.");
    } finally {
      loader(false);
    }
  };

  const handleCopyHtml = () => {
    if (!template?.body_html) return;
    navigator.clipboard?.writeText(template.body_html);
    setCopiedHtml(true);
    toast.success("HTML markup copied to clipboard.");
    setTimeout(() => setCopiedHtml(false), 2000);
  };

  if (loading || !template) {
    return (
      <Layout>
        <div className="py-24 text-center space-y-3">
          <div className="inline-block w-8 h-8 border-3 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
          <p className="text-xs font-semibold text-slate-500">Loading template dossier...</p>
        </div>
      </Layout>
    );
  }

  const typeConfig = EMAIL_TYPE_MAP[template.email_type] || {
    name: template.email_type || "CUSTOM",
    badgeClass: "bg-slate-100 text-slate-800 border-slate-300",
  };

  const effectiveHtml = React.useMemo(() => {
    if (!template?.body_html) return "";
    let rendered = String(template.body_html);
    const companyReplacements = {
      platform_name: user?.company_name || user?.name || "Charles S. Smith & Associates",
      platform_logo: user?.logo || "/assets/img/logo.png",
      platform_email: user?.email || "support@charlessmithassociates.com",
      platform_phone: user?.phone || "+1 (555) 019-2834",
      current_year: String(new Date().getFullYear()),
    };
    Object.keys(companyReplacements).forEach((k) => {
      const val = companyReplacements[k] || "N/A";
      const regex = new RegExp(`{{\\s*${k}\\s*}}`, "g");
      rendered = rendered.replace(regex, val);
    });
    return rendered;
  }, [template?.body_html, user]);

  const effectiveSubject = React.useMemo(() => {
    if (!template?.subject) return "";
    let rendered = String(template.subject);
    const companyName = user?.company_name || user?.name || "Charles S. Smith & Associates";
    rendered = rendered.replace(/{{\\s*platform_name\\s*}}/g, companyName || "N/A");
    return rendered;
  }, [template?.subject, user]);
  const variables = Array.isArray(template.variables) ? template.variables : [];

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & ACTIONS                                   */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2 rounded-xl text-slate-700 hover:text-slate-950 hover:bg-slate-100 border border-slate-200 transition-colors cursor-pointer shrink-0"
              title="Back to Email Templates"
            >
              <FiArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-black text-slate-900 truncate">
                  {template.name || "Untitled Template"}
                </h1>
                <span
                  className={`px-2.5 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${typeConfig.badgeClass}`}
                >
                  {typeConfig.name}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-medium truncate">
                Subject: <span className="font-semibold text-slate-800">{template.subject || "—"}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
            {canEdit && (
              <button
                type="button"
                onClick={() => navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`)}
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
              >
                <FiEdit3 className="w-4 h-4" />
                <span>Edit Template</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate(`${MODULE_CONFIG.routeBase}/create?clone=${id}`)}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all border border-slate-200 cursor-pointer"
              title="Duplicate this template"
            >
              <FiCopy className="w-4 h-4" />
              <span>Clone</span>
            </button>

            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-900 text-xs font-bold transition-all border border-rose-200 cursor-pointer"
                title="Delete template"
              >
                <FiTrash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. DEDICATED LIVE EMAIL PREVIEW VIEW                      */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] overflow-hidden">
          {/* Preview Toolbar */}
          <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/60">
            <div className="space-y-0.5">
              <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                Email Preview
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Live rendered template with simulated dynamic merge variables
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Device Switcher */}
              <div className="inline-flex rounded-xl bg-white p-1 border border-slate-200 shadow-2xs">
                <button
                  type="button"
                  onClick={() => setPreviewDevice("desktop")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    previewDevice === "desktop"
                      ? "bg-slate-900 text-white shadow-xs"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <FiMonitor className="w-3.5 h-3.5" />
                  <span>Desktop</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDevice("mobile")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    previewDevice === "mobile"
                      ? "bg-slate-900 text-white shadow-xs"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <FiSmartphone className="w-3.5 h-3.5" />
                  <span>Mobile</span>
                </button>
              </div>

              {/* Copy HTML Button */}
              <button
                type="button"
                onClick={handleCopyHtml}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 h-9 rounded-xl bg-white hover:bg-slate-100 text-slate-800 text-xs font-bold transition-colors cursor-pointer border border-slate-200 shadow-2xs"
                title="Copy full HTML code"
              >
                {copiedHtml ? <FiCheck className="w-3.5 h-3.5 text-emerald-600" /> : <FiCopy className="w-3.5 h-3.5 text-slate-600" />}
                <span>{copiedHtml ? "Copied HTML!" : "Copy HTML"}</span>
              </button>
            </div>
          </div>

          {/* Rendered Subject Banner */}
          <div className="px-5 py-3 bg-white border-b border-slate-200 flex items-center gap-3 text-xs">
            <span className="font-bold text-slate-500 uppercase tracking-wider text-[11px] shrink-0">
              Subject:
            </span>
            <span className="font-bold text-slate-900 truncate">
              {effectiveSubject || template.subject || "—"}
            </span>
          </div>

          {/* Canvas Wrapper */}
          <div className="p-6 sm:p-10 bg-slate-100/70 flex justify-center items-start min-h-[640px]">
            <div
              className={`transition-all duration-300 bg-white rounded-2xl shadow-xl border border-slate-300 overflow-hidden ${
                previewDevice === "mobile" ? "w-[375px] max-w-full" : "w-[640px] max-w-full"
              }`}
            >
              {/* Email Window Top Bar */}
              <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-rose-400 inline-block shadow-2xs" />
                  <span className="w-3 h-3 rounded-full bg-amber-400 inline-block shadow-2xs" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400 inline-block shadow-2xs" />
                </div>
                <div className="text-[11px] font-bold text-slate-500">
                  {previewDevice === "mobile" ? "Mobile Viewport (375px)" : "Desktop Viewport (640px)"}
                </div>
              </div>

              {/* Email Iframe Content */}
              <iframe
                title="Email Preview"
                srcDoc={effectiveHtml}
                className="w-full h-[620px] border-0 bg-white block"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
